import { FormHelperText, MenuItem, Select, SelectChangeEvent, TextField } from "@mui/material";
import { FormControl } from "@mui/material";
import { useTranslation } from "react-i18next";
import { LocationSelectors } from "../../../LocationSelectors";
import { ButtonForm } from "../../button-form";
import { useEffect, useMemo, useState } from "react";
import { Branch, ManagerPaycloud } from "../../../../types";
import { useToast } from "../../../../context/toast-context";
import { BranchService } from "../../../../services/branch-service";
import { ManagerService } from "../../../../services/manager-service";
import { DOCUMENT_LENGTHS } from "../../../../constants/validations";

interface EditManagerFormARProps {
  setIsModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  manager: ManagerPaycloud;
  fetchManagers?: () => Promise<void>;
}

/**
 * Formulario de edición de manager para Argentina
 * Incluye campos específicos de AR: CUIL, Provincia y Ciudad
 */
export function EditManagerFormAR({
  setIsModalOpen,
  manager,
  fetchManagers,
}: EditManagerFormARProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    ...manager,
    departmentId: manager.departmentId > 0 ? manager.departmentId : 0,
    cityId: manager.cityId > 0 ? manager.cityId : 0,
  });
  const { showToast } = useToast();
  const managerService = useMemo(() => new ManagerService(), []);
  const branchService = useMemo(() => new BranchService(), []);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedBranches, setSelectedBranches] = useState<number[]>(manager.branches.map(b => b.branchId));
  const [errors, setErrors] = useState({
      firstName: "",
      lastName: "",
      email: "",
      identityNumber: "",
      address: "",
      phone: "",
      departmentId: "",
      cityId: "",
      branchesIds: "",
    });

  // Sincronizar formData cuando cambia el manager
  useEffect(() => {
    setFormData({
      ...manager,
      departmentId: manager.departmentId > 0 ? manager.departmentId : 0,
      cityId: manager.cityId > 0 ? manager.cityId : 0,
    });
  }, [manager]);

  useEffect(() => {
    const loadBranches = async () => {
      try {
        await branchService.getBranches(
          { limit: 100, offset: 0 },
          (data: any) => {
            setBranches(data.branches);
          },
          (errorMessage: any) => {
            showToast(errorMessage, "error");
          }
        );
      } catch (error) {
        showToast(t("common.errors.generic"), "error");
      }
    };
  
      loadBranches();
    }, [branchService, showToast, t]);

  // Sincronizar sucursales seleccionadas cuando se carguen las branches
  useEffect(() => {
    if (branches.length > 0 && manager.branches && manager.branches.length > 0) {
      const managerBranchIds = manager.branches.map(b => b.branchId);
      setSelectedBranches(managerBranchIds);
      setFormData(prev => ({
        ...prev,
        branches: manager.branches
      }));
    }
  }, [branches, manager.branches]);

    const validate = () => {
      let isValid = true;
      const newErrors = {
        firstName: "",
        lastName: "",
        email: "",
        identityNumber: "",
        address: "",
        phone: "",
        departmentId: "",
        cityId: "",
        branchesIds: "",
      };
  
      if (!formData.firstName) {
        newErrors.firstName = t("managers.create.errors.name");
        isValid = false;
      }
  
      if (!formData.lastName) {
        newErrors.lastName = t("managers.create.errors.name");
        isValid = false;
      }
  
      if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = t("managers.create.errors.email");
        isValid = false;
      }
  
      if (!formData.identityNumber) {
        newErrors.identityNumber = t("managers.create.errors.identityNumber");
        isValid = false;
      } else if (formData.identityNumber.length !== DOCUMENT_LENGTHS.AR_CUIL_LENGTH) {
        newErrors.identityNumber = t("managers.create.errors.identityNumber-min");
        isValid = false;
      } else if (!formData.identityNumber.startsWith("20") && !formData.identityNumber.startsWith("23") && !formData.identityNumber.startsWith("24") && !formData.identityNumber.startsWith("27")) {
        newErrors.identityNumber = t("managers.create.errors.identityNumber-note");
        isValid = false;
      }
  
      if (!formData.address) {
        newErrors.address = t("managers.create.errors.address");
        isValid = false;
      } else if (formData.address.length < 5) {
        newErrors.address = t("managers.create.errors.address-min");
        isValid = false;
      } else if (formData.address.length > 50) {
        newErrors.address = t("managers.create.errors.address-max");
        isValid = false;
      }
  
      if (!formData.phone) {
        newErrors.phone = t("managers.create.errors.phone-required");
        isValid = false;
      } else if (formData.phone.length < 7 || formData.phone.length > 20) {
        newErrors.phone = t("managers.create.errors.phone-size");
        isValid = false;
      }
  
      if (formData.branches.length === 0) {
        newErrors.branchesIds = t("managers.create.errors.branchesIds");
        isValid = false;
      }
  
      if (!formData.departmentId) {
        newErrors.departmentId = t("managers.create.errors.departmentId");
        isValid = false;
      }
  
      if (!formData.cityId) {
        newErrors.cityId = t("managers.create.errors.cityId");
        isValid = false;
      }
  
      setErrors(newErrors);
      return isValid;
    };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setFormData((prev: typeof formData) => ({ ...prev, [name]: value }));
    };

const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
  e.preventDefault();
  setLoading(true);

  if (!validate()) {
    setLoading(false);
    return;
  }

  try {
    await managerService.updateManager(
      manager.userId,
      {
        FirstName: formData.firstName,
        LastName: formData.lastName,
        Email: manager.isVerified ? manager.email : formData.email,
        IdentityType: "CUIL",
        IdentityNumber: formData.identityNumber,
        Address: formData.address,
        Phone: formData.phone,
        DepartmentId: formData.departmentId.toString(),
        CityId: formData.cityId.toString(),
        BranchesIds: formData.branches.map(b => b.branchId),
        CountryCode: "AR",
      },
      async () => {
        setIsModalOpen && setIsModalOpen(false);
        showToast(t("managers.edit-success"), "success");
        if (fetchManagers) {
          await fetchManagers();
        }
      },
      (errorMessage : string) => {
        showToast(errorMessage, "error");
      }
    );
  } finally {
    setLoading(false);
  }
};

const handleBranchChange = (event: SelectChangeEvent<number[]>) => {
  const selectedOptions = event.target.value as number[];
  setSelectedBranches(selectedOptions);
  const selectedBranches = branches.filter(b => selectedOptions.includes(b.branchId));
  setFormData((prev: typeof formData) => ({ 
    ...prev, 
    branches: selectedBranches
  }));
};

const textFieldStyles = {
  "& .MuiInput-root": {
    color: "#000",
    fontFamily: "Montserrat",
    "&:before": {
      borderBottom: "1px solid #838383",
    },
    "&:hover:not(.Mui-disabled):before": {
      borderBottomColor: "#898989",
    },
  },
  "& label.Mui-focused": {
    color: "#7e7e7e",
  },
  "& .MuiInputLabel-standard": {
    color: "#000",
    fontWeight: "400",
    fontFamily: "Montserrat",
  },
  "& .MuiInput-underline:after": {
    borderBottomColor: "#838383",
  },
};

  return (
  <form onSubmit={handleSubmit} className="">
    <div className="bg-gray-light text-center text-xl font-normal text-black py-8 px-2">
      <h1>{t("managers.edit-title")}</h1>
    </div>
    <div className="grid gap-4 p-6">
      <TextField
        type="text"
        name="firstName"
        error={!!errors.firstName}
        helperText={errors.firstName}
        label={t("managers.create.name")}
        value={formData.firstName || ""}
        onChange={handleChange}
        variant="standard"
        sx={textFieldStyles}
      />

      <TextField
        type="text"
        name="lastName"
        error={!!errors.lastName}
        helperText={errors.lastName}
        label={t("managers.create.lastName")}
        value={formData.lastName || ""}
        onChange={handleChange}
        variant="standard"
        sx={textFieldStyles}
      />

      <TextField
        type="email"
        name="email"
        error={!!errors.email}
        helperText={errors.email}
        label={t("managers.create.email")}
        value={formData.email || ""}
        onChange={handleChange}
        disabled={manager.isVerified}
        variant="standard"
        sx={textFieldStyles}
      />

      <TextField
        type="text"
        name="identityNumber"
        error={!!errors.identityNumber}
        helperText={errors.identityNumber}
        label={"CUIL"}
        value={formData.identityNumber || ""}
        onChange={handleChange}
        variant="standard"
        sx={textFieldStyles}
      />

      <TextField
        type="text"
        name="address"
        error={!!errors.address}
        helperText={errors.address}
        label={t("managers.create.address")}
        value={formData.address || ""}
        onChange={handleChange}
        variant="standard"
        sx={textFieldStyles}
      />

      <TextField
        type="text"
        name="phone"
        error={!!errors.phone}
        helperText={errors.phone}
        label={t("managers.create.phone")}
        value={formData.phone || ""}
        onChange={handleChange}
        onInput={(e) => {
          const input = e.target as HTMLInputElement;
          let value = input.value;
          value = value.replace(/[^\d]/g, "");
          if (value.length > 20) {
            value = value.substring(0, 20);
          }
          input.value = value;
          setFormData(prev => ({ ...prev, phone: value }));
        }}
        variant="standard"
        sx={textFieldStyles}
      />

      <LocationSelectors
        initialDepartmentId={manager.departmentId > 0 ? manager.departmentId.toString() : ""}
        initialCityId={manager.cityId > 0 ? manager.cityId.toString() : ""}
        onProvinceChange={(provinceId) => {
          setFormData((prev: typeof formData) => ({
            ...prev,
            departmentId: parseInt(provinceId),
            cityId: 0
          }));
          setErrors(prev => ({ ...prev, departmentId: "" }));
        }}
        onCityChange={(cityId) => {
          setFormData((prev: typeof formData) => ({
            ...prev,
            cityId: parseInt(cityId)
          }));
          setErrors(prev => ({ ...prev, cityId: "" }));
        }}
      />
      {(errors.departmentId || errors.cityId) && (
        <p className="text-red text-xs mt-1">
          {errors.departmentId || errors.cityId}
        </p>
      )}

      <FormControl error={!!errors.branchesIds} variant="standard" fullWidth>
        <Select
          sx={{
            paddingX: 2,
            paddingY: 1,
            margin: 0,
            fontStyle: "normal",
            fontFamily: "Montserrat",
            fontSize: "1rem",
            height: "100%",
            width: "100%",
            borderRadius: "9999px",
            border: "1px solid",
            borderColor: "#838383",
            backgroundColor: "transparent",
            outline: "none",
            "&:focus": {
              outlineColor: "primary.main",
            },
            "& .MuiSelect-select": {
              padding: 0,
              display: "block",
              minWidth: 0,
            },
            "&.MuiInputBase-root.MuiOutlinedInput-root": {
              padding: "4px 8px",
            },
            "& .MuiSelect-select.MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled":
              {
                WebkitTextFillColor: "#838383",
                opacity: "70%",
              },
            "& .MuiSvgIcon-root.MuiSelect-icon": {
              color: "white",
            },
            color: "#000",
            fontWeight: "400",
            "&:before": {
              borderBottom: "none"
            },
            "&:after": {
              borderBottom: "none"
            },
            "&:hover:not(.Mui-disabled):before": {
              borderBottom: "none"
            },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                backgroundColor: "white",
                color: "#000",
                "& .MuiMenuItem-root": {
                  "&:hover": {
                    backgroundColor: "#f5f5f5"
                  },
                  "&.Mui-selected": {
                    backgroundColor: "#e0e0e0",
                    "&:hover": {
                      backgroundColor: "#d5d5d5"
                    }
                  }
                }
              },
            },
          }}
          multiple
          name="BranchesIds"
          value={selectedBranches}
          onChange={handleBranchChange}
          displayEmpty
          renderValue={(selected) => {
            if (selected.length === 0) {
              return (
                <span className="block truncate">
                  {t("managers.branchesIds")}
                </span>
              );
            }
            return selected
              .map((id) => branches.find((b) => b.branchId === id)?.name)
              .join(", ");
          }}
        >
          <MenuItem
            sx={{
              fontFamily: "Montserrat",
            }}
            disabled
            value=""
          >
            <span>{t("managers.branchesIds")}</span>              
          </MenuItem>
          {branches.map((branch) => (
            <MenuItem
              sx={{
                fontFamily: "Montserrat",
              }}
              key={`branch-${branch.branchId}`}
              value={branch.branchId}
            >
              {branch.name}
            </MenuItem>
          ))}
        </Select>
        {errors.branchesIds && (
          <FormHelperText>{errors.branchesIds}</FormHelperText>
        )}
      </FormControl>

      <div className="w-full flex flex-col gap-4">
        <ButtonForm loading={loading}>
          {t("managers.edit-submit")}
        </ButtonForm>
      </div>
    </div>
  </form>
  
  );
}

